import { useApolloClient } from '@apollo/client';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Flex,
  HStack,
  IconButton,
  Link,
  Text,
  Tooltip,
  useDisclosure,
  usePrevious,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { Input, NumberInput, Switch } from '@gamma/ui/form-fields';
import CheckCircleSharpIcon from '@gamma/ui/icons/CheckCircleSharpIcon';
import DeleteSharpIcon from '@gamma/ui/icons/DeleteSharpIcon';
import ErrorSharpIcon from '@gamma/ui/icons/ErrorSharpIcon';
import HelpSharpIcon from '@gamma/ui/icons/HelpSharpIcon';
import VisibilityOffSharpIcon from '@gamma/ui/icons/VisibilityOffSharpIcon';
import VisibilitySharpIcon from '@gamma/ui/icons/VisibilitySharpIcon';
import WarningSharpIcon from '@gamma/ui/icons/WarningSharpIcon';
import { Panel } from '@gamma/ui/layout';
import { Alert as GammaAlert, Modal } from '@gamma/ui/overlay';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import { isEqual } from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { URLS } from '../../../../shared/constants';
import { useLogOperationCall } from '../../../../shared/hooks';
import { i18n } from '../../../../shared/localization';
import { AuthContext } from '../../../../shared/providers/AuthProvider';
import {
  GET_EDR_CONFIGURATION,
  IVerifyEDRConfiguration,
  useDeleteEDRConfiguration,
  useGetEDRConfiguration,
  useGetEDRConfigurationPublic,
  useStoreEDRConfiguration,
  useVerifyEDRConfiguration,
} from '../../../entities/queries';

const { integrations } = i18n.pages;
const {
  save,
  cancel,
  error: errorText,
  success,
  warning,
  paloAlto: { verifyError },
} = integrations;

const {
  noGoBack,
  yesDelete,
  enabled: enabledLabel,
  disabled: disabledLabel,
  connectionNotVerified,
  connectionVerified: connectionVerifiedMessage,
} = integrations;

const {
  saveSuccess,
  saveError,
  deleteError,
  deleteSuccess,
  isolateEntity,
  isolateHelpText,
  isolateEnabledAlert,
  verifyConnection,
  deleteResult,
  deleteWarning,
  pollingHelpText,
  enterCredentials,
  msdefenderIntegration,
  msdefenderFormFields,
  msdefenderDocumentation,
  deleteMSDefenderConnection,
  detectionsCanNotBeEscalatedToMSDefender,
} = integrations.msdefender;

const { receiveEntityEnrichment } = integrations;

const { DEFENDER_DOCS } = URLS;

interface MSDefenderSettingsCardModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const MSDefenderSettingsCardModal = ({
  isOpen,
  onClose,
}: MSDefenderSettingsCardModalProps) => {
  const showToast = useToast();

  const { userRole, setUserPrivileges, userPrivileges, userLicense } =
    useContext(AuthContext);
  const { tenant = '' } = userLicense ?? {};

  const emptyForm = {
    enabled: false,
    edr_action_enabled: false,
    app_id: '',
    app_secret: '',
    defender_tenant_id: '',
    polling_seconds: 30,
  };

  const [formIsReady, setFormIsReady] = useState<boolean>(false);
  const client = useApolloClient();

  const logOperationCall = useLogOperationCall();

  const getEDRConfigQuery =
    userRole === 'admin'
      ? useGetEDRConfiguration
      : useGetEDRConfigurationPublic;

  const {
    loading: EDRConfigLoading,
    error: EDRConfigError,
    data: EDRData,
  } = getEDRConfigQuery({
    variables: {
      provider_name: 'microsoft_defender',
    },
    fetchPolicy: 'network-only',
    skip: !isOpen,
    onCompleted: (data) => {
      if (data.getEDRConfiguration === null) {
        reset(emptyForm);
        setFormIsReady(true);
      } else {
        const {
          provider_name: _,
          polling_seconds,
          ...rest
        } = data.getEDRConfiguration;
        reset({ ...rest, polling_seconds: polling_seconds / 60 });
        setFormIsReady(true);
      }
    },
  });

  const {
    watch,
    control,
    reset,
    register,
    formState: { isDirty, isValid },
  } = useForm();

  const formData = watch();
  const previousFormData = usePrevious(formData);

  const {
    isOpen: deleteIsOpen,
    onOpen: deleteOnOpen,
    onClose: deleteOnClose,
  } = useDisclosure();

  const [appSecretInputPwType, setAppSecretInputPwType] =
    useState<boolean>(true);

  const [configurationVerified, setConfigurationVerified] = useState<
    boolean | undefined
  >();

  const [verifyEDRConfiguration, { loading: verifyEDRConfigurationLoading }] =
    useVerifyEDRConfiguration({
      fetchPolicy: 'network-only',
      onCompleted: (data: IVerifyEDRConfiguration) => {
        setConfigurationVerified(data?.verifyEDRConfiguration?.verified);
      },
      onError: () => {
        showToast({
          title: errorText,
          description: verifyError,
          status: 'error',
          isClosable: true,
          position: 'bottom-right',
        });
      },
    });

  const [storeEDRConfiguration, { loading: storeEDRConfigurationLoading }] =
    useStoreEDRConfiguration({
      onCompleted: async () => {
        client.cache.evict({
          id: 'ROOT_QUERY',
          fieldName: 'getEDRConfiguration',
        });
        client.cache.gc();
        showToast({
          title: success,
          description: saveSuccess,
          status: 'success',
          isClosable: true,
          position: 'bottom-right',
        });
        const updatedInput = {
          status: formData.enabled ? 'Enabled' : 'Disabled',
          service_name: 'Microsoft Defender',
        };

        logOperationCall(
          'ENABLE_DISABLE_CONFIGURATION',
          'INTEGRATION',
          JSON.stringify(updatedInput),
        );
        const newUserPrivileges = { ...userPrivileges };
        newUserPrivileges[tenant ?? ''].microsoft_defender = formData.enabled;
        newUserPrivileges[tenant ?? ''].microsoft_defender_action_enabled =
          formData.edr_action_enabled;
        setUserPrivileges(newUserPrivileges);
        handleClose();
      },
      onError: async () => {
        showToast({
          title: errorText,
          description: saveError,
          status: 'error',
          isClosable: true,
          position: 'bottom-right',
        });
      },
    });

  const [deleteEDRConfiguration, { loading: deleteEDRConfigurationLoading }] =
    useDeleteEDRConfiguration({
      variables: {
        provider_name: 'microsoft_defender',
      },
      refetchQueries: [
        {
          query: GET_EDR_CONFIGURATION,
          variables: {
            provider_name: 'microsoft_defender',
          },
        },
      ],
      onCompleted: async () => {
        showToast({
          title: success,
          description: deleteSuccess,
          status: 'success',
          isClosable: true,
          position: 'bottom-right',
        });
        const newUserPrivileges = { ...userPrivileges };
        newUserPrivileges[tenant ?? ''].microsoft_defender = false;
        newUserPrivileges[tenant ?? ''].microsoft_defender_action_enabled =
          false;
        setUserPrivileges(newUserPrivileges);
        reset(emptyForm);
        onClose();
      },
      onError: async () => {
        showToast({
          title: errorText,
          description: deleteError,
          status: 'error',
          isClosable: true,
          position: 'bottom-right',
        });
      },
    });

  const handleClose = () => {
    if (EDRData?.getEDRConfiguration === null) {
      reset(emptyForm);
    } else if (EDRData?.getEDRConfiguration) {
      const { provider_name: _, ...rest } = EDRData.getEDRConfiguration;
      reset({ ...rest });
    }
    onClose();
  };

  useEffect(() => {
    if (
      previousFormData !== undefined &&
      !isEqual(previousFormData, formData)
    ) {
      setConfigurationVerified(undefined);
    }
  }, [formData, previousFormData]);

  return EDRConfigError || EDRConfigLoading || !formIsReady ? (
    <GraphQLReqStatus
      error={EDRConfigError}
      loading={EDRConfigLoading || !formIsReady}
      isBackground={true}
    ></GraphQLReqStatus>
  ) : (
    <>
      <Modal
        size="lg"
        isOpen={isOpen}
        onClose={handleClose}
        isCentered={true}
        title={msdefenderIntegration}
        closeOnEsc={!verifyEDRConfigurationLoading}
        isCloseDisabled={verifyEDRConfigurationLoading}
        closeOnOverlayClick={!verifyEDRConfigurationLoading}
        body={
          <VStack as="form" alignItems="flex-start" spacing={4}>
            <Text textStyle="body-md">{receiveEntityEnrichment}</Text>
            <Controller
              name="enabled"
              control={control}
              render={({ field: { onChange, value, name } }) => (
                <Switch
                  inline
                  name={name}
                  isChecked={value}
                  isDisabled={false}
                  onChange={(e) => {
                    onChange(e);
                  }}
                  label={formData.enabled ? enabledLabel : disabledLabel}
                />
              )}
            />
            <Panel layerStyle="second">
              <VStack alignItems="start">
                <Tooltip placement="end" label={isolateHelpText}>
                  <HStack>
                    <Text textStyle="body-md">{isolateEntity}</Text>
                    <HelpSharpIcon color="text.primary" size="sm" />
                  </HStack>
                </Tooltip>
                <Controller
                  name="edr_action_enabled"
                  control={control}
                  render={({ field: { onChange, value, name } }) => (
                    <Switch
                      inline
                      name={name}
                      isChecked={value}
                      isDisabled={false}
                      label={
                        formData.edr_action_enabled
                          ? enabledLabel
                          : disabledLabel
                      }
                      onChange={(e) => {
                        onChange(e);
                      }}
                    />
                  )}
                />
                {formData?.edr_action_enabled && (
                  <GammaAlert variant="subtle" status="warning">
                    {isolateEnabledAlert}
                  </GammaAlert>
                )}
              </VStack>
            </Panel>
            <Box>
              <Text>
                {`${enterCredentials} `}
                <Link
                  isExternal
                  href={DEFENDER_DOCS}
                  textDecoration="underline"
                >
                  {msdefenderDocumentation}
                </Link>
                .
              </Text>
            </Box>
            <Input
              {...register('defender_tenant_id', {
                required: true,
              })}
              isRequired={true}
              isDisabled={false}
              label={msdefenderFormFields.defenderTenantId}
            />
            <Input
              {...register('app_id', {
                required: true,
              })}
              isRequired={true}
              isDisabled={false}
              label={msdefenderFormFields.appId}
            />
            <Input
              {...register('app_secret', {
                required: true,
              })}
              isRequired={true}
              isDisabled={false}
              label={msdefenderFormFields.appSecret}
              type={appSecretInputPwType ? 'password' : 'text'}
              rightElement={
                <IconButton
                  cursor="pointer"
                  aria-label={`${appSecretInputPwType ? 'Show' : 'Hide'} Client ID`}
                  onClick={() => setAppSecretInputPwType(!appSecretInputPwType)}
                >
                  {appSecretInputPwType ? (
                    <VisibilitySharpIcon size="sm" />
                  ) : (
                    <VisibilityOffSharpIcon size="sm" />
                  )}
                </IconButton>
              }
            />
            <Controller
              name="polling_seconds"
              control={control}
              rules={{
                min: 30,
                max: 1440,
                required: true,
              }}
              render={({ field: { onChange, value, name } }) => (
                <NumberInput
                  min={30}
                  max={1440}
                  name={name}
                  value={value}
                  stepper={true}
                  isRequired={true}
                  isDisabled={false}
                  tooltip={pollingHelpText}
                  hint={msdefenderFormFields.minThirtyMinutes}
                  label={msdefenderFormFields.pollingMinutes}
                  onChange={(value) => onChange(Number(value))}
                />
              )}
            />
            <ButtonGroup
              w="100%"
              justifyContent="space-between"
              isDisabled={!isValid || configurationVerified}
            >
              <Button
                variant="solid"
                colorScheme="gray"
                leftIcon={<CheckCircleSharpIcon />}
                onClick={() => {
                  verifyEDRConfiguration({
                    variables: {
                      provider_name: 'microsoft_defender',
                      config: {
                        microsoft_defender: {
                          ...formData,
                          polling_seconds: formData.polling_seconds * 60,
                        },
                      },
                    },
                  });
                }}
                isDisabled={!isValid || configurationVerified}
                isLoading={verifyEDRConfigurationLoading}
              >
                {verifyConnection}
              </Button>
              <Tooltip label={deleteMSDefenderConnection}>
                <IconButton
                  variant="solid"
                  colorScheme={deleteEDRConfigurationLoading ? 'gray' : 'red'}
                  onClick={deleteOnOpen}
                  icon={<DeleteSharpIcon />}
                  aria-label={deleteMSDefenderConnection}
                  isLoading={deleteEDRConfigurationLoading}
                  isDisabled={deleteEDRConfigurationLoading || !isValid}
                />
              </Tooltip>
            </ButtonGroup>
            {configurationVerified === true && (
              <Alert status="success" w="100%">
                <Flex>
                  <CheckCircleSharpIcon color="green.100" mr={2} />
                  <Box>
                    <Text textStyle="body-md-bold">
                      {connectionVerifiedMessage}
                    </Text>
                  </Box>
                </Flex>
              </Alert>
            )}
            {configurationVerified === false && (
              <Alert status="error" w="100%">
                <Flex>
                  <WarningSharpIcon color="red.100" mr={2} />
                  <Box>
                    <Text textStyle="body-md-bold">
                      {connectionNotVerified}
                    </Text>
                    <Text>{detectionsCanNotBeEscalatedToMSDefender}</Text>
                  </Box>
                </Flex>
              </Alert>
            )}
          </VStack>
        }
        footer={
          <ButtonGroup isDisabled={storeEDRConfigurationLoading}>
            <Button variant="solid" colorScheme="gray" onClick={handleClose}>
              {cancel}
            </Button>
            <Button
              variant="solid"
              onClick={() => {
                storeEDRConfiguration({
                  variables: {
                    provider_name: 'microsoft_defender',
                    config: {
                      microsoft_defender: {
                        ...formData,
                        polling_seconds: formData.polling_seconds * 60,
                      },
                    },
                  },
                });
              }}
              isLoading={storeEDRConfigurationLoading}
              isDisabled={
                storeEDRConfigurationLoading ||
                !(isDirty && isValid) ||
                (formData?.enabled && !configurationVerified)
              }
            >
              {save}
            </Button>
          </ButtonGroup>
        }
      />
      <Modal
        size="lg"
        isOpen={deleteIsOpen}
        onClose={deleteOnClose}
        title="Delete Microsoft Defender Connection"
        body={
          <Alert variant="subtle" status="warning" w="100%">
            <Flex>
              <ErrorSharpIcon color="yellow.100" mr={2} />
              <Box>
                <Text textStyle="body-md-bold">{warning}</Text>
                <Text>{deleteWarning}</Text>
                <Text>{deleteResult}</Text>
              </Box>
            </Flex>
          </Alert>
        }
        footer={
          <ButtonGroup isDisabled={deleteEDRConfigurationLoading}>
            <Button variant="solid" colorScheme="gray" onClick={deleteOnClose}>
              {noGoBack}
            </Button>
            <Button
              variant="solid"
              colorScheme="red"
              onClick={() => {
                deleteEDRConfiguration();
                deleteOnClose();
              }}
              isLoading={deleteEDRConfigurationLoading}
              isDisabled={deleteEDRConfigurationLoading}
            >
              {yesDelete}
            </Button>
          </ButtonGroup>
        }
      />
    </>
  );
};
